import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const hpValue = (form.elements.namedItem("phone_alt") as HTMLInputElement)?.value?.trim();

    if (hpValue) {
      setStatus("success");
      setEmail("");
      return;
    }

    if (!email.trim()) {
      setErrorMsg("Proszę wpisać adres email.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const formData = new FormData(form);
      const res = await fetch("https://readdy.ai/api/form/d9cfnjmdn0rfb35c5lv0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && (parsed as { code?: string })?.code === "OK") {
        setStatus("success");
        setEmail("");
      } else {
        const msg = ((parsed as { meta?: { message?: string } })?.meta?.message)
          || ((parsed as { message?: string })?.message)
          || responseText
          || "Wystąpił błąd. Spróbuj ponownie.";
        setErrorMsg(msg.includes("spam") ? "Wystąpił błąd. Spróbuj ponownie." : msg);
        setStatus("error");
      }
    } catch {
      setErrorMsg("Wystąpił błąd. Spróbuj ponownie.");
      setStatus("error");
    }
  };

  return (
    <footer className="bg-background-100 border-t border-background-200/70">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-[40%]">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight">
              {t("footer_title_line1")}<br />{t("footer_title_line2")}
            </h2>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed max-w-sm">
              {t("footer_subtitle")}
            </p>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-8 max-w-sm"
            >
              <label className="block text-xs font-medium text-foreground-500 mb-2">
                {t("footer_newsletter_label")}
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="twój@email.com"
                  className="flex-1 bg-transparent border-b border-foreground-300 px-0 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                  required
                />
                <input
                  type="text"
                  name="phone_alt"
                  data-hp="1"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="px-6 py-2 border border-foreground-800 text-foreground-800 text-sm font-medium rounded-xl hover:bg-foreground-800 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {status === "loading" ? "..." : t("footer_newsletter_button")}
                </button>
              </div>
              {status === "success" && (
                <p className="mt-2 text-xs text-accent-600">Dziękujemy za zapisanie się!</p>
              )}
              {status === "error" && errorMsg && (
                <p className="mt-2 text-xs text-secondary-600">{errorMsg}</p>
              )}
            </form>
          </div>

          <div className="lg:w-[60%] grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-4">{t("footer_col1_title")}</h3>
              <div className="flex flex-col gap-2.5">
                <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col1_link1")}</a>
                <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col1_link2")}</a>
                <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col1_link3")}</a>
                <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col1_link4")}</a>
                <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col1_link5")}</a>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-4">{t("footer_col2_title")}</h3>
              <div className="flex flex-col gap-2.5">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col2_link1")}</a>
                <Link to="/artykul/kakobuy-spreadsheet-dla-poczatkujacych" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col2_link2")}</Link>
                <Link to="/artykul/kakobuy-bezpieczenstwo-zakupow" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col2_link3")}</Link>
                <Link to="/artykul/kakobuy-wysylka-polska" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col2_link4")}</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-4">{t("footer_col3_title")}</h3>
              <div className="flex flex-col gap-2.5">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">{t("footer_col3_link1")}</a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-background-200/70">
          <p className="text-xs text-foreground-500">{t("footer_copyright")}</p>
        </div>
      </div>
    </footer>
  );
}